import { useFormikContext } from 'formik'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { CollapsibleBox } from '@/components/collapsible'
import { Label, FieldInput, FieldSwitchToggle, FieldDatePicker } from '@/components/form'
import { fontWeights } from '@/styles'
import { FormGrid, FormRow } from '../../elements'

const DescriptionText = styled.p`
  font-size: 14px;
  line-height: 19px;
  margin: 0;

  span {
    font-weight: ${fontWeights.medium};
  }
`

const handleAllowPets = (values, setFieldValue) => {
  if (values.pet_policy.is_allowed) {
    setFieldValue('pet_policy.allow_cats', null)
    setFieldValue('pet_policy.allow_dogs', null)
    setFieldValue('pet_policy.pet_limitations', false)
    setFieldValue('pet_policy.pet_deposit', '0')
  }
  setFieldValue('pet_policy.is_allowed', !values.pet_policy.is_allowed)
}

const handlePetLimitations = (values, setFieldValue) => {
  setFieldValue('pet_policy.allow_cats', values.pet_policy.pet_limitations ? null : 1)
  setFieldValue('pet_policy.allow_dogs', values.pet_policy.pet_limitations ? null : 1)
  setFieldValue('pet_policy.pet_limitations', !values.pet_policy.pet_limitations)
}

const PetPolicySection = ({ act }) => {
  const { t } = useTranslation()
  const { setFieldValue, values: data } = useFormikContext()

  const initialMonth = () => (data.start_date ? moment(data.start_date) : null)
  const isOntarioContract = act.state === 'ON'
  const isBCContract = act.state === 'BC'

  return (
    <CollapsibleBox title={t('l.contract.edit.pet_policy.label', 'Pet Policy')} maxLength={22}>
      <FormGrid>
        <FormRow>
          <Label noGutter bottom="0">
            {t('l.contract.edit.pets_allowed.label', 'Pets allowed')}
          </Label>
          <FieldSwitchToggle
            name="pet_policy.is_allowed"
            onChange={() => handleAllowPets(data, setFieldValue)}
          />
        </FormRow>
        {isOntarioContract && (
          <FormRow>
            <DescriptionText>
              {t(
                'l.contract.edit.ontario_pet_policy_disclaimer.text',
                'In Ontario, no‑pets clauses in rental agreements are void. This means they cannot be enforced.'
              )}
            </DescriptionText>
          </FormRow>
        )}
        {!!data.pet_policy.is_allowed && (
          <>
            <FormRow>
              <Label noGutter bottom="0">
                {t('l.contract.edit.set_restrictions.label', 'Set restrictions')}
              </Label>
              <FieldSwitchToggle
                name="pet_policy.pet_limitations"
                onChange={() => handlePetLimitations(data, setFieldValue)}
              />
            </FormRow>
            {!!data.pet_policy.pet_limitations && (
              <>
                <div>
                  <FieldInput
                    name="pet_policy.allow_cats"
                    placeholder={t('l.contract.edit.max_cats.label', 'Max. cats')}
                    required
                  />
                </div>
                <div>
                  <FieldInput
                    name="pet_policy.allow_dogs"
                    placeholder={t('l.contract.edit.max_dogs.label', 'Max. dogs')}
                    required
                  />
                </div>
              </>
            )}
            <div>
              <FieldInput
                name="pet_policy.pet_deposit"
                placeholder={t('l.contract.edit.pet_deposit_amount.label', 'Pet deposit amount')}
                prefix="$"
                transform={val => (val ?? '').replace(/[^0-9.]/g, '')}
              />
            </div>
            {isBCContract && (
              <>
                <DescriptionText>
                  {t(
                    'l.contract.edit.pet_deposit_disclaimer.text',
                    'By law, the Pet Deposit Amount cannot be more than half of the Monthly Rental Price.'
                  )}
                </DescriptionText>
                <FieldDatePicker
                  name="pet_policy.pet_deposit_due_date"
                  placeholder={t('l.contract.edit.pet_deposit_due_date.label', 'Due date')}
                  initialFocusedDate={initialMonth()}
                />
              </>
            )}
          </>
        )}
      </FormGrid>
    </CollapsibleBox>
  )
}

export default PetPolicySection
